/**
 * Serverzustand der Genehmigungen.
 *
 * Nach jeder Entscheidung wird **auch der Ticketverlauf** neu geholt: Der
 * Server schreibt einen Systembeitrag ans Ticket ("Genehmigung erteilt"), und
 * eine Ansicht, die ihn erst beim naechsten Wechsel zeigt, laesst den
 * Bearbeiter zweifeln, ob seine Entscheidung angekommen ist.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../lib/apiError';
import { ticketKeys } from '../queries';
import {
  cancelApproval,
  createApproval,
  decideApproval,
  listApprovals,
  listPendingApprovals,
  type ApprovalCreate,
} from './api';

export const approvalKeys = {
  forTicket: (ticketId: string) => ['tickets', 'approvals', ticketId] as const,
  pending: ['approvals', 'pending'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useApprovals(ticketId: string) {
  return useQuery({
    queryKey: approvalKeys.forTicket(ticketId),
    queryFn: () => listApprovals(ticketId),
    retry: retryUnlessPermanent,
  });
}

export function usePendingApprovals() {
  return useQuery({
    queryKey: approvalKeys.pending,
    queryFn: listPendingApprovals,
    retry: retryUnlessPermanent,
  });
}

function useAfterChange(ticketId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: approvalKeys.forTicket(ticketId) });
    await queryClient.invalidateQueries({ queryKey: approvalKeys.pending });
    await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
  };
}

export function useCreateApproval(ticketId: string) {
  const afterChange = useAfterChange(ticketId);
  return useMutation({
    mutationFn: (payload: ApprovalCreate) => createApproval(ticketId, payload),
    onSuccess: afterChange,
  });
}

export function useDecideApproval(ticketId: string) {
  const afterChange = useAfterChange(ticketId);
  return useMutation({
    mutationFn: ({
      requestId,
      approved,
      comment,
    }: {
      requestId: string;
      approved: boolean;
      comment: string | null;
    }) => decideApproval(requestId, { approved, comment }),
    onSuccess: afterChange,
  });
}

export function useCancelApproval(ticketId: string) {
  const afterChange = useAfterChange(ticketId);
  return useMutation({
    mutationFn: (requestId: string) => cancelApproval(requestId),
    onSuccess: afterChange,
  });
}
